import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { Pill } from '../../Pill.js';
import type { ResultSelection } from '../../workflows.types.js';
import { Copy, SUCCESS_PILL_SX, selectionSummary } from '../primitives.js';
import type { EditableSceneField } from './SceneCard.js';
import { SceneTable } from './SceneTable.js';
import { SceneTimeline } from './SceneTimeline.js';
import { StoryboardVersionCard } from './StoryboardVersionCard.js';
import type { Scene, StoryboardResult } from './storyboardResult.js';

type StoryboardDrafts = Scene[][];

function initialDrafts(result: StoryboardResult): StoryboardDrafts {
  return result.versions.map((version) => version.scenes.map((scene) => ({ ...scene })));
}

export function StoryboardDrawer({
  result,
  open,
  onClose,
  initialVersion,
  selection,
  actions,
  className,
}: {
  result: StoryboardResult;
  open: boolean;
  onClose: () => void;
  initialVersion: number;
  selection?: ResultSelection;
  /** Footer buttons beside Close, such as download and share when viewing a saved outcome. */
  actions?: ReactNode;
  className?: string;
}): ReactNode {
  const [at, setAt] = useState(initialVersion);
  const [layout, setLayout] = useState<'strip' | 'table'>('strip');
  const [drafts, setDrafts] = useState<StoryboardDrafts>(() => initialDrafts(result));
  const version = result.versions[at];
  const board = result.storyboard;
  const scenes = version === undefined ? [] : (drafts[at] ?? version.scenes);
  const displayedVersion = version === undefined ? undefined : { ...version, scenes };
  const selected = version !== undefined && selection?.selectedIds.includes(version.name);
  const updateScene = (index: number, field: EditableSceneField, value: string): void => {
    if (version === undefined) return;
    setDrafts((current) => {
      const next = [...current];
      next[at] = scenes.map((scene, sceneIndex) =>
        sceneIndex === index ? { ...scene, [field]: value } : scene,
      );
      return next;
    });
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      {...(className === undefined ? {} : { className })}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(98,116,142,0.4)' } },
        paper: {
          role: 'dialog',
          'aria-label': 'Storyboard versions',
          sx: { width: 'min(820px, 100vw)', bgcolor: 'background.paper', borderRadius: 0 },
        },
      }}
    >
      <Stack direction="row" sx={{ p: '24px', gap: '12px', alignItems: 'center' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '9px',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #F0FFF6 3.125%, #CCE9E7 100%)',
          }}
        >
          <Box
            component="img"
            src="/images/workflows/storyboard.svg"
            alt=""
            sx={{ position: 'absolute', left: 8, top: 9, width: 16.25, height: 13.75 }}
          />
        </Box>
        <Stack sx={{ gap: '4px', flex: 1, minWidth: 0 }}>
          <Stack direction="row" sx={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Storyboard versions</Typography>
            <Pill
              sx={(theme) => ({
                ...SUCCESS_PILL_SX(theme),
                borderRadius: '6px',
                px: '6px',
                py: '4px',
              })}
            >
              SAVED
            </Pill>
          </Stack>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
            {[
              ...(board.source_brief === undefined ? [] : [board.source_brief.name]),
              `${board.format} format`,
              ...(board.duration_seconds === null ? [] : [`${board.duration_seconds} sec`]),
              `KPI: ${board.primary_kpi_label || board.primary_kpi}`,
            ].join(' · ')}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close storyboard"
          sx={{ width: 40, height: 40, displayPrint: 'none' }}
        >
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Tabs
        value={version === undefined ? false : at}
        onChange={(_, value: number) => setAt(value)}
        aria-label="Storyboard versions"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: '24px',
          flexShrink: 0,
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {result.versions.map((entry, index) => (
          <Tab
            key={`${entry.name}-${entry.strategy}-${entry.creative_premise}`}
            id={`storyboard-tab-${index}`}
            aria-controls="storyboard-panel"
            label={`Version ${index + 1}`}
            sx={{ fontSize: 12, minHeight: 40, minWidth: 0, px: '12px' }}
          />
        ))}
      </Tabs>
      <Stack
        id="storyboard-panel"
        role="tabpanel"
        aria-labelledby={`storyboard-tab-${at}`}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: 'background.page',
          p: { xs: '16px', sm: '16px 24px' },
          gap: '20px',
          '@media print': { overflowY: 'visible' },
        }}
      >
        {version === undefined ? (
          <Alert severity="info">No storyboard versions were returned.</Alert>
        ) : (
          <>
            <Stack
              direction="row"
              sx={{ gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {version.rationale}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={layout}
                onChange={(_, value: 'strip' | 'table' | null) => {
                  if (value !== null) setLayout(value);
                }}
                aria-label="Storyboard layout"
                sx={{
                  flexShrink: 0,
                  p: '4px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '9px',
                  '& .MuiToggleButton-root': {
                    border: 0,
                    borderRadius: '6px',
                    fontSize: 10,
                    px: '10px',
                    minHeight: 40,
                    '&.Mui-selected': {
                      bgcolor: 'background.icon',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'background.icon' },
                    },
                  },
                }}
              >
                <ToggleButton value="strip">Strip</ToggleButton>
                <ToggleButton value="table">Table</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {board.warnings.map((warning) => (
              <Alert key={warning} severity="warning">
                {warning}
              </Alert>
            ))}
            {selection === undefined ? null : (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                Edits are local drafts for this review and are not saved to the storyboard.
              </Typography>
            )}
            <SceneTimeline scenes={scenes} />
            {layout === 'strip' && displayedVersion !== undefined ? (
              <StoryboardVersionCard
                version={displayedVersion}
                versionKey={at}
                storyboard={board}
                editable={selection !== undefined}
                onSceneChange={updateScene}
              />
            ) : (
              <SceneTable scenes={scenes} />
            )}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Storyboard context</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Stack sx={{ gap: 2 }}>
                  <Copy label="Production handoff">{board.body}</Copy>
                  <Copy label="Context">{board.context}</Copy>
                  <Copy label="Production instructions">{board.production_instructions}</Copy>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{
          px: '24px',
          py: '16px',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          borderTop: '1px solid',
          borderColor: 'divider',
          displayPrint: 'none',
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
          {selection === undefined
            ? ''
            : selectionSummary(selection.selectedIds.length, result.versions.length)}
        </Typography>
        {actions}
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        {selection === undefined || version === undefined ? null : (
          <Button
            variant="gradient"
            onClick={() => {
              selection.onToggle(version.name);
              onClose();
            }}
          >
            {selected ? 'Deselect' : 'Select'} Version {at + 1}
          </Button>
        )}
      </Stack>
    </Drawer>
  );
}
